import { FC, forwardRef, HTMLProps, useEffect, useState } from 'react';
import { cx } from '@emotion/css';
import { Label } from '../label/label';
import { getHintBorderColor, Hint, HintProps } from '../hint';
import { useTranslation } from 'next-i18next';
import { cn } from '@phoenix/design-system/frontend/util/classNameUtils';

export type TextAreaProps = HTMLProps<HTMLTextAreaElement> & {
  description?: string;
  hint?: HintProps;
  displayCharCounter?: boolean;
  counterClassName?: string;
  containerClassName?: string;
  placeholderText?: string;
};

const DEFAULT_MAX_CHARACTERS = 500;

export const TextArea: FC<React.PropsWithChildren<TextAreaProps>> = forwardRef(
  (
    {
      description,
      label,
      hint,
      disabled = false,
      displayCharCounter = false,
      counterClassName = 'right-0',
      containerClassName,
      className = '',
      value,
      onChange,
      placeholderText,
      ...textAreaProps
    },
    ref
  ) => {
    const { t } = useTranslation('shared');

    const [charCounter, setCharCounter] = useState(0);

    useEffect(() => {
      setCharCounter(value && typeof value === 'string' ? value.length : 0);
    }, [value]);

    const placeholderValue = () =>
      typeof placeholderText === 'string' ? placeholderText : t('shared_textarea_placeholder', { maxCharacters: textAreaProps.maxLength });

    return (
      <div className={cx('flex flex-col stihl-text-body-default-reg', containerClassName)}>
        {label && <Label disabled={disabled} text={label} />}
        {description && <div className="mt-1 stihl-text-body-xs-reg">{description}</div>}
        <div className="relative mt-1">
          <textarea
            className={cx('inline-block w-full align-top', getHintBorderColor(hint?.type), !!description && 'mt-1', className)}
            {...textAreaProps}
            value={value}
            onChange={(e) => {
              onChange?.(e);
              setCharCounter(e.target.value.length);
            }}
            placeholder={placeholderValue()}
            maxLength={textAreaProps.maxLength}
            disabled={disabled}
            ref={ref}
          />
          <div className="relative mt-1 flex items-center justify-between">
            {hint && <Hint {...hint} className={cn(hint?.className, displayCharCounter && 'pr-20')} />}
            {displayCharCounter ? (
              <span className={cx('absolute right-0 top-1 text-right text-secondary-dark', counterClassName)}>
                {charCounter} / {textAreaProps.maxLength || DEFAULT_MAX_CHARACTERS}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);
